// ArChat Presence — Presets sugeridos (18 idiomas)
// Created: 16-04-2026
// Author: SIVOL® - Aldo Frias - Argentina
// Fuente única para alias de matching + spread en translations.*.js
// Las claves cortas (K) deben coincidir con backend/lib/PresencePresetRegistry.php y GET /api/presence/presets.php

const K = {
  coffee: 'presence.custom.suggestion.coffee',
  soccer: 'presence.custom.suggestion.soccer',
  working: 'presence.custom.suggestion.working',
  lunch: 'presence.custom.suggestion.lunch',
  family: 'presence.custom.suggestion.family',
  studying: 'presence.custom.suggestion.studying',
  travel: 'presence.custom.suggestion.travel',
  meeting: 'presence.custom.suggestion.meeting',
  resting: 'presence.custom.suggestion.resting',
  gaming: 'presence.custom.suggestion.gaming',
  reading: 'presence.custom.suggestion.reading',
  offlineSoon: 'presence.custom.suggestion.offlineSoon',
};

/** IDs cortos canónicos (coffee, lunch, offlineSoon, …) → clave i18n completa. */
export const PRESET_SHORT_TO_I18N_KEY = K;

/** Orden estable para UI (mismo orden que K). */
export const PRESET_SHORT_KEYS = Object.keys(K);

/**
 * @param {unknown} key
 * @returns {boolean}
 */
export function isValidPresetShortKey(key) {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(K, key);
}

/**
 * Textos canónicos por idioma (emoji incluido al final cuando aplica).
 * Deben coincidir con lo que devuelve t(key) en cada locale tras spread.
 */
export const PRESET_LOCALES = {
  es: {
    [K.coffee]: 'Tomando unos mates ☕',
    [K.soccer]: 'En la cancha ⚽',
    [K.working]: 'Laburando como loco 💪',
    [K.lunch]: 'Almorzando 🍽️',
    [K.family]: 'Almuerzo familiar 👨‍👩‍👧‍👦',
    [K.studying]: 'Estudiando 📚',
    [K.travel]: 'De viaje ✈️',
    [K.meeting]: 'En reunión 💼',
    [K.resting]: 'Descansando 😴',
    [K.gaming]: 'Jugando videojuegos 🎮',
    [K.reading]: 'Leyendo 📖',
    [K.offlineSoon]: 'Vuelvo enseguida ⏳',
  },
  en: {
    [K.coffee]: 'Having coffee ☕',
    [K.soccer]: 'At the soccer field ⚽',
    [K.working]: 'Heads down working 💪',
    [K.lunch]: 'Having lunch 🍽️',
    [K.family]: 'Family lunch 👨‍👩‍👧‍👦',
    [K.studying]: 'Studying 📚',
    [K.travel]: 'Traveling ✈️',
    [K.meeting]: 'In a meeting 💼',
    [K.resting]: 'Resting 😴',
    [K.gaming]: 'Playing videogames 🎮',
    [K.reading]: 'Reading 📖',
    [K.offlineSoon]: 'Back soon ⏳',
  },
  pt: {
    [K.coffee]: 'Tomando um café ☕',
    [K.soccer]: 'No campo ⚽',
    [K.working]: 'Trabalhando focado 💪',
    [K.lunch]: 'No almoço 🍽️',
    [K.family]: 'Almoço em família 👨‍👩‍👧‍👦',
    [K.studying]: 'Estudando 📚',
    [K.travel]: 'Em viagem ✈️',
    [K.meeting]: 'Em reunião 💼',
    [K.resting]: 'Descansando 😴',
    [K.gaming]: 'Jogando videogame 🎮',
    [K.reading]: 'Lendo 📖',
    [K.offlineSoon]: 'Volto já ⏳',
  },
  fr: {
    [K.coffee]: 'Au café ☕',
    [K.soccer]: 'Au terrain ⚽',
    [K.working]: 'En plein travail 💪',
    [K.lunch]: 'Au déjeuner 🍽️',
    [K.family]: 'Déjeuner en famille 👨‍👩‍👧‍👦',
    [K.studying]: 'En train d’étudier 📚',
    [K.travel]: 'En voyage ✈️',
    [K.meeting]: 'En réunion 💼',
    [K.resting]: 'Au repos 😴',
    [K.gaming]: 'En train de jouer 🎮',
    [K.reading]: 'En train de lire 📖',
    [K.offlineSoon]: 'Je reviens vite ⏳',
  },
  it: {
    [K.coffee]: 'Al caffè ☕',
    [K.soccer]: 'In campo ⚽',
    [K.working]: 'A lavoro 💪',
    [K.lunch]: 'A pranzo 🍽️',
    [K.family]: 'Pranzo in famiglia 👨‍👩‍👧‍👦',
    [K.studying]: 'Studio 📚',
    [K.travel]: 'In viaggio ✈️',
    [K.meeting]: 'In riunione 💼',
    [K.resting]: 'Mi riposo 😴',
    [K.gaming]: 'Gioco ai videogiochi 🎮',
    [K.reading]: 'Leggo 📖',
    [K.offlineSoon]: 'Torno subito ⏳',
  },
  de: {
    [K.coffee]: 'Kaffee trinken ☕',
    [K.soccer]: 'Auf dem Platz ⚽',
    [K.working]: 'Am Arbeiten 💪',
    [K.lunch]: 'Beim Mittagessen 🍽️',
    [K.family]: 'Familienmittagessen 👨‍👩‍👧‍👦',
    [K.studying]: 'Beim Lernen 📚',
    [K.travel]: 'Unterwegs ✈️',
    [K.meeting]: 'In einer Besprechung 💼',
    [K.resting]: 'Ruhepause 😴',
    [K.gaming]: 'Zocken 🎮',
    [K.reading]: 'Lesen 📖',
    [K.offlineSoon]: 'Bin gleich zurück ⏳',
  },
  nl: {
    [K.coffee]: 'Koffie drinken ☕',
    [K.soccer]: 'Op het veld ⚽',
    [K.working]: 'Aan het werk 💪',
    [K.lunch]: 'Aan het lunchen 🍽️',
    [K.family]: 'Gezinslunch 👨‍👩‍👧‍👦',
    [K.studying]: 'Aan het studeren 📚',
    [K.travel]: 'Op reis ✈️',
    [K.meeting]: 'In vergadering 💼',
    [K.resting]: 'Rusten 😴',
    [K.gaming]: 'Gamen 🎮',
    [K.reading]: 'Lezen 📖',
    [K.offlineSoon]: 'Zo terug ⏳',
  },
  pl: {
    [K.coffee]: 'Piję kawę ☕',
    [K.soccer]: 'Na boisku ⚽',
    [K.working]: 'Pracuję 💪',
    [K.lunch]: 'Na obiedzie 🍽️',
    [K.family]: 'Rodzinny obiad 👨‍👩‍👧‍👦',
    [K.studying]: 'Uczę się 📚',
    [K.travel]: 'W podróży ✈️',
    [K.meeting]: 'Na spotkaniu 💼',
    [K.resting]: 'Odpoczywam 😴',
    [K.gaming]: 'Gram 🎮',
    [K.reading]: 'Czytam 📖',
    [K.offlineSoon]: 'Zaraz wracam ⏳',
  },
  ru: {
    [K.coffee]: 'Пью кофе ☕',
    [K.soccer]: 'На поле ⚽',
    [K.working]: 'За работой 💪',
    [K.lunch]: 'Обедаю 🍽️',
    [K.family]: 'Семейный обед 👨‍👩‍👧‍👦',
    [K.studying]: 'Учусь 📚',
    [K.travel]: 'В пути ✈️',
    [K.meeting]: 'На встрече 💼',
    [K.resting]: 'Отдыхаю 😴',
    [K.gaming]: 'Играю 🎮',
    [K.reading]: 'Читаю 📖',
    [K.offlineSoon]: 'Скоро вернусь ⏳',
  },
  el: {
    [K.coffee]: 'Πίνω καφέ ☕',
    [K.soccer]: 'Στο γήπεδο ⚽',
    [K.working]: 'Δουλεύω 💪',
    [K.lunch]: 'Στο μεσημεριανό 🍽️',
    [K.family]: 'Οικογενειακό μεσημεριανό 👨‍👩‍👧‍👦',
    [K.studying]: 'Διαβάζω 📚',
    [K.travel]: 'Ταξιδεύω ✈️',
    [K.meeting]: 'Σε συνάντηση 💼',
    [K.resting]: 'Ξεκουράζομαι 😴',
    [K.gaming]: 'Παίζω 🎮',
    [K.reading]: 'Διαβάζω βιβλίο 📖',
    [K.offlineSoon]: 'Γυρίζω σύντομα ⏳',
  },
  tr: {
    [K.coffee]: 'Kahve içiyorum ☕',
    [K.soccer]: 'Sahada ⚽',
    [K.working]: 'Çalışıyorum 💪',
    [K.lunch]: 'Öğle yemeğinde 🍽️',
    [K.family]: 'Aile yemeği 👨‍👩‍👧‍👦',
    [K.studying]: 'Ders çalışıyorum 📚',
    [K.travel]: 'Yoldayım ✈️',
    [K.meeting]: 'Toplantıdayım 💼',
    [K.resting]: 'Dinleniyorum 😴',
    [K.gaming]: 'Oyun oynuyorum 🎮',
    [K.reading]: 'Okuyorum 📖',
    [K.offlineSoon]: 'Hemen dönerim ⏳',
  },
  ar: {
    [K.coffee]: 'أشرب القهوة ☕',
    [K.soccer]: 'في الملعب ⚽',
    [K.working]: 'أعمل 💪',
    [K.lunch]: 'في الغداء 🍽️',
    [K.family]: 'غداء عائلي 👨‍👩‍👧‍👦',
    [K.studying]: 'أدرس 📚',
    [K.travel]: 'مسافر ✈️',
    [K.meeting]: 'في اجتماع 💼',
    [K.resting]: 'أستريح 😴',
    [K.gaming]: 'ألعب 🎮',
    [K.reading]: 'أقرأ 📖',
    [K.offlineSoon]: 'أعود قريباً ⏳',
  },
  he: {
    [K.coffee]: 'שותה קפה ☕',
    [K.soccer]: 'במגרש ⚽',
    [K.working]: 'עובד 💪',
    [K.lunch]: 'בארוחת צהריים 🍽️',
    [K.family]: 'ארוחת צהריים משפחתית 👨‍👩‍👧‍👦',
    [K.studying]: 'לומד 📚',
    [K.travel]: 'בדרך ✈️',
    [K.meeting]: 'בפגישה 💼',
    [K.resting]: 'נח 😴',
    [K.gaming]: 'משחק 🎮',
    [K.reading]: 'קורא 📖',
    [K.offlineSoon]: 'חוזר מיד ⏳',
  },
  hi: {
    [K.coffee]: 'कॉफी पी रहा हूँ ☕',
    [K.soccer]: 'मैदान पर ⚽',
    [K.working]: 'काम कर रहा हूँ 💪',
    [K.lunch]: 'दोपहर का भोजन 🍽️',
    [K.family]: 'पारिवारिक भोजन 👨‍👩‍👧‍👦',
    [K.studying]: 'पढ़ाई 📚',
    [K.travel]: 'यात्रा में ✈️',
    [K.meeting]: 'मीटिंग में 💼',
    [K.resting]: 'आराम 😴',
    [K.gaming]: 'गेम खेल रहा हूँ 🎮',
    [K.reading]: 'पढ़ रहा हूँ 📖',
    [K.offlineSoon]: 'जल्द वापस ⏳',
  },
  ja: {
    [K.coffee]: 'コーヒー中 ☕',
    [K.soccer]: 'グラウンドで ⚽',
    [K.working]: '作業中 💪',
    [K.lunch]: '昼食中 🍽️',
    [K.family]: '家族の昼食 👨‍👩‍👧‍👦',
    [K.studying]: '勉強中 📚',
    [K.travel]: '移動中 ✈️',
    [K.meeting]: '会議中 💼',
    [K.resting]: '休憩中 😴',
    [K.gaming]: 'ゲーム中 🎮',
    [K.reading]: '読書中 📖',
    [K.offlineSoon]: 'すぐ戻ります ⏳',
  },
  ko: {
    [K.coffee]: '커피 마시는 중 ☕',
    [K.soccer]: '경기장에서 ⚽',
    [K.working]: '작업 중 💪',
    [K.lunch]: '점심 식사 중 🍽️',
    [K.family]: '가족과 점심 👨‍👩‍👧‍👦',
    [K.studying]: '공부 중 📚',
    [K.travel]: '이동 중 ✈️',
    [K.meeting]: '회의 중 💼',
    [K.resting]: '휴식 중 😴',
    [K.gaming]: '게임 중 🎮',
    [K.reading]: '독서 중 📖',
    [K.offlineSoon]: '곧 돌아옵니다 ⏳',
  },
  zh: {
    [K.coffee]: '喝咖啡 ☕',
    [K.soccer]: '在球场 ⚽',
    [K.working]: '工作中 💪',
    [K.lunch]: '午餐中 🍽️',
    [K.family]: '家庭午餐 👨‍👩‍👧‍👦',
    [K.studying]: '学习中 📚',
    [K.travel]: '出行中 ✈️',
    [K.meeting]: '会议中 💼',
    [K.resting]: '休息中 😴',
    [K.gaming]: '游戏中 🎮',
    [K.reading]: '阅读中 📖',
    [K.offlineSoon]: '马上回来 ⏳',
  },
  sv: {
    [K.coffee]: 'Dricker kaffe ☕',
    [K.soccer]: 'På planen ⚽',
    [K.working]: 'Jobbar 💪',
    [K.lunch]: 'På lunch 🍽️',
    [K.family]: 'Familjelunch 👨‍👩‍👧‍👦',
    [K.studying]: 'Pluggar 📚',
    [K.travel]: 'På resa ✈️',
    [K.meeting]: 'I möte 💼',
    [K.resting]: 'Vilar 😴',
    [K.gaming]: 'Spelar 🎮',
    [K.reading]: 'Läser 📖',
    [K.offlineSoon]: 'Strax tillbaka ⏳',
  },
};

/** Misma lista que CustomStatusModal (orden estable). */
export const PRESENCE_CUSTOM_SUGGESTION_KEYS = Object.keys(PRESET_LOCALES.es);
